import React, { createContext, useContext, useState, useCallback } from 'react';

export type Language = 'en' | 'hi';

// Full dictionary, merged from App.tsx so there is exactly one source of
// truth — App.tsx should import LanguageProvider/useLanguage from this file
// instead of defining its own (see APP_TSX_PATCH_INSTRUCTIONS.md, step 6).
export const translations: Record<Language, Record<string, string>> = {
  en: {
    newChat: 'New Chat', plugins: 'Plugins', scheduledTasks: 'Scheduled Tasks',
    collapse: 'Collapse', miraalWorkspace: 'Miraal Workspace', miraalCode: 'Miraal Code',
    upgradePlan: 'Upgrade Plan', aboutMiraal: 'About Miraal', language: 'Language',
    getHelp: 'Get Help', settings: 'Settings', chats: 'Chats', search: 'Search',
    noChats: 'No chats yet', plans: 'Plans', subscription: 'Subscription',
    theme: 'Theme', general: 'General', chat: 'Chat', manage: 'Manage',
    presets: 'Presets', expandSidebar: 'Expand Sidebar on Search',
    system: 'System', english: 'English', hindi: 'Hindi',
    chooseTier: 'Choose your Miraal.ai intelligence tier', annually: 'Annually',
    saveUpTo: 'Save up to 20%', subscribe: 'Subscribe', free: 'Free',
    perYear: '/ year', perMonth: '/ month', monthly: 'Monthly',
    aiRequests: 'AI Requests / mo', concurrentTasks: 'Concurrent Tasks',
    processingPriority: 'Processing Priority', agentSwarm: 'Agent Swarm',
    memoryRetention: 'Memory Retention', miraalCodeFeature: 'Miraal Code', support: 'Support',
    standard: 'Standard', realtime: 'Realtime', unlimited: 'Unlimited',
    community: 'Community', email: 'Email', priority: 'Priority',
    dedicated: 'Dedicated', whiteGlove: 'White-glove',
    day: 'day', days: 'days', uses: 'uses', subagents: 'subagents',
    credits: 'credits', task: 'task', tasks: 'tasks', speed: 'speed',
    disclaimer: 'All plans include Miraal core capabilities. Higher tiers unlock advanced features.',
    visitMiraal: 'Visit Miraal.ai', miraalPlatform: 'Miraal Platform',
    features: 'Features', termsOfService: 'Terms of Service', privacyPolicy: 'Privacy Policy',
    helpCenter: 'Help Center', whatIsMiraal: 'What is Miraal.ai?',
    contactUs: 'Contact Us', sendMessage: 'Send Message', yourName: 'Your name',
    yourEmail: 'your@email.com', howCanWeHelp: 'How can we help?', messageSent: 'Message Sent!',
    weWillReply: 'We will get back to you within 24 hours', feedback: 'Feedback',
    improveMiraal: 'Help us improve Miraal.ai', submit: 'Submit', thankYou: 'Thank You!',
    version: 'Version', logout: 'Logout', loggedIn: 'Logged in', currentPlan: 'Current Plan',
    viewAllPlans: 'View All Plans', upgrade: 'Upgrade', installed: 'Installed', install: 'Install',
    extendMiraal: 'Extend Miraal with powerful tools', automateWorkflows: 'Automate recurring workflows',
    noTasksYet: 'No scheduled tasks yet', createFirstTask: 'Create your first automated workflow',
    planDetails: 'Plan Details', usage: 'Usage', remaining: 'Remaining',
    yourPlan: 'Your Plan', planFeatures: 'Plan Features', close: 'Close',
    account: 'Account', notifications: 'Notifications', security: 'Security',
    appearance: 'Appearance', dataPrivacy: 'Data & Privacy', apiKeys: 'API Keys',
    connectedApps: 'Connected Apps', sessionManagement: 'Session Management',
    profile: 'Profile', phoneNumber: 'Phone Number', emailAddress: 'Email Address',
    changePassword: 'Change Password', twoFactorAuth: 'Two-Factor Authentication',
    activeSessions: 'Active Sessions', deleteAccount: 'Delete Account',
    pushNotifications: 'Push Notifications', emailNotifications: 'Email Notifications',
    marketingEmails: 'Marketing Emails', darkMode: 'Dark Mode', lightMode: 'Light Mode',
    fontSize: 'Font Size', compact: 'Compact', comfortable: 'Comfortable',
    exportData: 'Export Data', clearHistory: 'Clear History', autoDelete: 'Auto-Delete Chats',
    save: 'Save', cancel: 'Cancel', edit: 'Edit', done: 'Done',
    agentCredits: 'Agent Credits', agentConcurrentTasks: 'Agent Concurrent Tasks',
    agentSpeedPriority: 'Agent Speed Priority', agentSwarmUses: 'Agent Swarm Uses',
    swarmSubtasks: 'Swarm Subtasks', miraalCodeCredits: 'Miraal Code Credits',
    miraalClaw: 'Miraal Claw', professionalDB: 'Professional Database Calls',
    youSave: 'You save', mostPopular: 'Most Popular', recommended: 'Recommended',
    enterprise: 'Enterprise', contactSales: 'Contact Sales', customPricing: 'Custom pricing for teams',
    heyDemo: "Hey {name}, let's build something.", createPresentation: 'Create a presentation about',
    agent: 'Agent', slides: 'Slides', deepResearch: 'Deep Research', docs: 'Docs',
    websites: 'Websites', sheets: 'Sheets', liveReminders: 'Live Reminders', completed: 'Completed',
    miraalToolStack: 'Miraal.ai Tool Stack', swarmDemo: 'Agent Swarm Demo', assignTask: 'Assign Task',
    taskAssigned: 'Task assigned to', processing: 'Processing',
    researchAgent: 'Research Agent', codeAgent: 'Code Agent', designAgent: 'Design Agent',
    writingAgent: 'Writing Agent', swarmActive: 'Swarm Active', agentsWorking: 'agents working',
    typeMessage: 'Type a message...', send: 'Send', thinking: 'Thinking...',
    ganga: 'Ganga', yamuna: 'Yamuna', brahmaputra: 'Brahmaputra', kaveri: 'Kaveri',
    godavari: 'Godavari', narmada: 'Narmada', krishna: 'Krishna', mahanadi: 'Mahanadi',
    tapti: 'Tapti',
    orchestration: 'Orchestration', dependencies: 'Dependencies', waitingFor: 'Waiting for',
    websiteExample: 'Build a Website: Agent Swarm', step1: '1. Research Agent analyzes requirements',
    step2: '2. Design Agent creates UI (waits for Research)', step3: '3. Code Agent builds frontend (waits for Design)',
    step4: '4. Backend Agent builds API (waits for Research)', step5: '5. DevOps Agent deploys (waits for Code + Backend)',
    parallel: 'Parallel', sequential: 'Sequential', dependsOn: 'Depends on',
    orchestrator: 'Orchestrator', taskQueue: 'Task Queue', executionGraph: 'Execution Graph',
    attachFiles: 'Attach Files', moreTools: 'More Tools', generateImage: 'Generate Image',
    webSearch: 'Web Search', codeInterpreter: 'Code Interpreter',
    dashboard: 'Dashboard', documents: 'Documents', teams: 'Teams',
    miraalExplore: 'Miraal Explore', miraalSwarm: 'Miraal Swarm',
    bollywoodScript: 'Bollywood Script', pythonAutomation: 'Python Automation',
    niftyAnalysis: 'NIFTY 50 Analysis', createWebsite: 'Create a Website',
    startConversation: 'Start a conversation',
    messagesSaveLocally: 'Messages save locally and to backend when connected',
    selectModel: 'Select Model',
    selectMode: 'Mode',
    models: 'Models',
    voiceInput: 'Voice Input',
    listening: 'Listening...',
    tapToSpeak: 'Tap to speak',
    newProject: 'New Project',
    fileUpload: 'File Upload',
    photoUpload: 'Photo Upload',
    webSearchTool: 'Web Search',
    deepResearchTool: 'Deep Research',
    attachments: 'Attachments',
    dragDropFiles: 'Drag & drop files here, or click to browse',
    supportedFormats: 'Supported: PDF, DOC, TXT, JPG, PNG, MP3, MP4, and more',
    uploadComplete: 'Upload complete',
    uploadFailed: 'Upload failed',
    fileTooLarge: 'File too large',
    maxFileSize: 'Max file size: 100MB',
    emojiReactions: 'Reactions',
    reactWithEmoji: 'React with emoji',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    passwordChanged: 'Password changed successfully',
    passwordMismatch: 'Passwords do not match',
    enterPassword: 'Enter password',
    confirmNewPassword: 'Confirm new password',
    twoFAEnabled: 'Two-Factor Authentication enabled',
    twoFADisabled: 'Two-Factor Authentication disabled',
    accountDeleted: 'Account deletion requested',
    historyCleared: 'Chat history cleared',
    dataExported: 'Data exported successfully',
    sessionsCleared: 'All sessions revoked',
    enterName: 'Enter your name',
    enterEmail: 'Enter your email',
    enterPhone: 'Enter your phone number',
  },
  hi: {
    newChat: 'नई चैट', plugins: 'प्लगइन्स', scheduledTasks: 'निर्धारित कार्य',
    collapse: 'संक्षिप्त करें', miraalWorkspace: 'मिराल वर्कस्पेस', miraalCode: 'मिराल कोड',
    upgradePlan: 'प्लान अपग्रेड करें', aboutMiraal: 'मिराल के बारे में', language: 'भाषा',
    getHelp: 'सहायता प्राप्त करें', settings: 'सेटिंग्स', chats: 'चैट्स', search: 'खोजें',
    noChats: 'अभी तक कोई चैट नहीं', plans: 'प्लान्स', subscription: 'सदस्यता',
    theme: 'थीम', general: 'सामान्य', chat: 'चैट', manage: 'प्रबंधन',
    presets: 'प्रीसेट्स', expandSidebar: 'खोज पर साइडबार बड़ा करें',
    system: 'सिस्टम', english: 'English', hindi: 'हिन्दी',
    chooseTier: 'अपनी मिराल.ai इंटेलिजेंस टियर चुनें', annually: 'वार्षिक',
    saveUpTo: '20% तक बचाएं', subscribe: 'सब्सक्राइब', free: 'मुफ्त',
    perYear: '/ वर्ष', perMonth: '/ माह', monthly: 'मासिक',
    aiRequests: 'AI अनुरोध / माह', concurrentTasks: 'समवर्ती कार्य',
    processingPriority: 'प्रोसेसिंग प्राथमिकता', agentSwarm: 'एजेंट स्वार्म',
    memoryRetention: 'मेमोरी रिटेंशन', miraalCodeFeature: 'मिराल कोड', support: 'सहायता',
    standard: 'स्टैंडर्ड', realtime: 'रीलटाइम', unlimited: 'असीमित',
    community: 'सामुदायिक', email: 'ईमेल', priority: 'प्राथमिकता',
    dedicated: 'समर्पित', whiteGlove: 'व्हाइट-ग्लव',
    day: 'दिन', days: 'दिन', uses: 'उपयोग', subagents: 'सबएजेंट्स',
    credits: 'क्रेडिट्स', task: 'कार्य', tasks: 'कार्य', speed: 'गति',
    disclaimer: 'सभी प्लान में मिराल की मुख्य क्षमताएं शामिल हैं।',
    visitMiraal: 'मिराल.ai पर जाएं', miraalPlatform: 'मिराल प्लेटफॉर्म',
    features: 'सुविधाएं', termsOfService: 'सेवा की शर्तें', privacyPolicy: 'गोपनीयता नीति',
    helpCenter: 'सहायता केंद्र', whatIsMiraal: 'मिराल.ai क्या है?',
    contactUs: 'संपर्क करें', sendMessage: 'संदेश भेजें', yourName: 'आपका नाम',
    yourEmail: 'your@email.com', howCanWeHelp: 'हम कैसे मदद कर सकते हैं?', messageSent: 'संदेश भेज दिया गया!',
    weWillReply: 'हम 24 घंटे के भीतर जवाब देंगे', feedback: 'प्रतिक्रिया',
    improveMiraal: 'मिराल.ai में सुधार में मदद करें', submit: 'जमा करें', thankYou: 'धन्यवाद!',
    version: 'संस्करण', logout: 'लॉग आउट', loggedIn: 'लॉग इन', currentPlan: 'वर्तमान प्लान',
    viewAllPlans: 'सभी प्लान देखें', upgrade: 'अपग्रेड', installed: 'इंस्टॉल्ड', install: 'इंस्टॉल करें',
    extendMiraal: 'शक्तिशाली टूल के साथ मिराल बढ़ाएं', automateWorkflows: 'आवर्ती वर्कफ़्लो स्वचालित करें',
    noTasksYet: 'अभी तक कोई निर्धारित कार्य नहीं', createFirstTask: 'अपना पहला स्वचालित वर्कफ़्लो बनाएं',
    planDetails: 'प्लान विवरण', usage: 'उपयोग', remaining: 'बचा हुआ',
    yourPlan: 'आपका प्लान', planFeatures: 'प्लान सुविधाएं', close: 'बंद करें',
    account: 'खाता', notifications: 'सूचनाएं', security: 'सुरक्षा',
    appearance: 'रूप', dataPrivacy: 'डेटा और गोपनीयता', apiKeys: 'API कुंजी',
    connectedApps: 'जुड़े ऐप्स', sessionManagement: 'सत्र प्रबंधन',
    profile: 'प्रोफाइल', phoneNumber: 'फोन नंबर', emailAddress: 'ईमेल पता',
    changePassword: 'पासवर्ड बदलें', twoFactorAuth: 'दो-कारक प्रमाणीकरण',
    activeSessions: 'सक्रिय सत्र', deleteAccount: 'खाता हटाएं',
    pushNotifications: 'पुश सूचनाएं', emailNotifications: 'ईमेल सूचनाएं',
    marketingEmails: 'मार्केटिंग ईमेल', darkMode: 'डार्क मोड', lightMode: 'लाइट मोड',
    fontSize: 'फ़ॉन्ट आकार', compact: 'संक्षिप्त', comfortable: 'आरामदायक',
    exportData: 'डेटा निर्यात', clearHistory: 'इतिहास साफ़ करें', autoDelete: 'ऑटो-डिलीट चैट',
    save: 'सहेजें', cancel: 'रद्द करें', edit: 'संपादित', done: 'हो गया',
    agentCredits: 'एजेंट क्रेडिट्स', agentConcurrentTasks: 'एजेंट समवर्ती कार्य',
    agentSpeedPriority: 'एजेंट गति प्राथमिकता', agentSwarmUses: 'एजेंट स्वार्म उपयोग',
    swarmSubtasks: 'स्वार्म सबटास्क', miraalCodeCredits: 'मिराल कोड क्रेडिट्स',
    miraalClaw: 'मिराल क्लॉ', professionalDB: 'प्रोफेशनल डेटाबेस कॉल्स',
    youSave: 'आप बचाते हैं', mostPopular: 'सबसे लोकप्रिय', recommended: 'अनुशंसित',
    enterprise: 'एंटरप्राइज', contactSales: 'बिक्री से संपर्क करें', customPricing: 'टीम के लिए कस्टम मूल्य निर्धारण',
    heyDemo: 'हैं {name}, चलो कुछ बनाते हैं।', createPresentation: 'के बारे में एक प्रस्तुति बनाएं',
    agent: 'एजेंट', slides: 'स्लाइड्स', deepResearch: 'डीप रिसर्च', docs: 'दस्तावेज़',
    websites: 'वेबसाइट्स', sheets: 'शीट्स', liveReminders: 'लाइव रिमाइंडर', completed: 'पूर्ण',
    miraalToolStack: 'मिराल.ai टूल स्टैक', swarmDemo: 'एजेंट स्वार्म डेमो', assignTask: 'कार्य सौंपें',
    taskAssigned: 'कार्य सौंपा गया', processing: 'प्रोसेसिंग',
    researchAgent: 'रिसर्च एजेंट', codeAgent: 'कोड एजेंट', designAgent: 'डिज़ाइन एजेंट',
    writingAgent: 'राइटिंग एजेंट', swarmActive: 'स्वार्म सक्रिय', agentsWorking: 'एजेंट काम कर रहे हैं',
    typeMessage: 'संदेश लिखें...', send: 'भेजें', thinking: 'सोच रहा है...',
    ganga: 'गंगा', yamuna: 'यमुना', brahmaputra: 'ब्रह्मपुत्र', kaveri: 'कावेरी',
    godavari: 'गोदावरी', narmada: 'नर्मदा', krishna: 'कृष्णा', mahanadi: 'महानदी',
    tapti: 'तापती',
    orchestration: 'ऑर्केस्ट्रेशन', dependencies: 'निर्भरताएं', waitingFor: 'इंतजार कर रहा है',
    websiteExample: 'वेबसाइट बनाएं: एजेंट स्वार्म', step1: '1. रिसर्च एजेंट आवश्यकताएं विश्लेषण करता है',
    step2: '2. डिज़ाइन एजेंट UI बनाता है (रिसर्च का इंतजार)', step3: '3. कोड एजेंट फ्रंटएंड बनाता है (डिज़ाइन का इंतजार)',
    step4: '4. बैकएंड एजेंट API बनाता है (रिसर्च का इंतजार)', step5: '5. DevOps एजेंट डिप्लॉय करता है (कोड + बैकएंड का इंतजार)',
    parallel: 'समानांतर', sequential: 'क्रमिक', dependsOn: 'निर्भर है',
    orchestrator: 'ऑर्केस्ट्रेटर', taskQueue: 'कार्य कतार', executionGraph: 'एक्जीक्यूशन ग्राफ',
    attachFiles: 'फाइलें जोड़ें', moreTools: 'और टूल्स', generateImage: 'इमेज जनरेट करें',
    webSearch: 'वेब खोज', codeInterpreter: 'कोड इंटरप्रेटर',
    dashboard: 'डैशबोर्ड', documents: 'दस्तावेज़', teams: 'टीम्स',
    miraalExplore: 'मिराल एक्सप्लोर', miraalSwarm: 'मिराल स्वार्म',
    bollywoodScript: 'बॉलीवुड स्क्रिप्ट', pythonAutomation: 'पायथन ऑटोमेशन',
    niftyAnalysis: 'NIFTY 50 विश्लेषण', createWebsite: 'वेबसाइट बनाएं',
    startConversation: 'बातचीत शुरू करें',
    messagesSaveLocally: 'संदेश स्थानीय रूप से सहेजे जाते हैं और बैकएंड से जुड़ने पर सिंक होते हैं',
    selectModel: 'मॉडल चुनें',
    selectMode: 'मोड',
    models: 'मॉडल्स',
    voiceInput: 'वॉइस इनपुट',
    listening: 'सुन रहा है...',
    tapToSpeak: 'बोलने के लिए टैप करें',
    newProject: 'नया प्रोजेक्ट',
    fileUpload: 'फ़ाइल अपलोड',
    photoUpload: 'फ़ोटो अपलोड',
    webSearchTool: 'वेब खोज',
    deepResearchTool: 'डीप रिसर्च',
    attachments: 'अटैचमेंट्स',
    dragDropFiles: 'फ़ाइलें यहाँ खींचें और छोड़ें, या ब्राउज़ करने के लिए क्लिक करें',
    supportedFormats: 'समर्थित: PDF, DOC, TXT, JPG, PNG, MP3, MP4, और अन्य',
    uploadComplete: 'अपलोड पूर्ण',
    uploadFailed: 'अपलोड विफल',
    fileTooLarge: 'फाइल बहुत बड़ी है',
    maxFileSize: 'अधिकतम फाइल आकार: 100MB',
    emojiReactions: 'प्रतिक्रियाएं',
    reactWithEmoji: 'इमोजी के साथ प्रतिक्रिया दें',
    currentPassword: 'वर्तमान पासवर्ड',
    newPassword: 'नया पासवर्ड',
    confirmPassword: 'नया पासवर्ड पुष्टि करें',
    passwordChanged: 'पासवर्ड सफलतापूर्वक बदल दिया गया',
    passwordMismatch: 'पासवर्ड मेल नहीं खाते',
    enterPassword: 'पासवर्ड दर्ज करें',
    confirmNewPassword: 'नए पासवर्ड की पुष्टि करें',
    twoFAEnabled: 'दो-कारक प्रमाणीकरण सक्षम',
    twoFADisabled: 'दो-कारक प्रमाणीकरण अक्षम',
    accountDeleted: 'खाता हटाने का अनुरोध किया गया',
    historyCleared: 'चैट इतिहास साफ़ किया गया',
    dataExported: 'डेटा सफलतापूर्वक निर्यात किया गया',
    sessionsCleared: 'सभी सत्र रद्द किए गए',
    enterName: 'अपना नाम दर्ज करें',
    enterEmail: 'अपना ईमेल दर्ज करें',
    enterPhone: 'अपना फोन नंबर दर्ज करें',
  }
};

interface LanguageContextType {
  lang: Language;
  t: (key: string, vars?: Record<string, string>) => string;
  setLang: (l: Language) => void;
}

const LanguageContext = createContext<LanguageContextType>({ lang: 'en', t: (k) => k, setLang: () => {} });

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>(() => (localStorage.getItem('miraal_language') as Language) || 'en');

  const setLang = useCallback((l: Language) => {
    setLangState(l);
    localStorage.setItem('miraal_language', l);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => {
      let text = translations[lang][key] || key;
      if (vars) Object.entries(vars).forEach(([k, v]) => { text = text.replace(`{${k}}`, v); });
      return text;
    },
    [lang]
  );

  return <LanguageContext.Provider value={{ lang, t, setLang }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
